import {
	Box,
	Button,
	IconButton,
	Skeleton,
	Stack,
	Tooltip,
	Typography,
	tooltipClasses,
	useTheme,
} from '@mui/material';
import React from 'react';

import {AlertInfo} from 'presentation/components/AlertInfo';
import {DateFormatEnum, ProfileEnum} from 'presentation/constants';
import {useContractStatus} from 'presentation/hooks';
import Icon from 'presentation/icons';
import {planTypeOptions, useAccountInformation} from 'presentation/pages';
import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';
import {translations} from 'presentation/translations-new';
import {formatText, getInitials, stringfyDate} from 'presentation/utils';
import {ProfileMenuEnum} from '../../../../../../../tests/constants/ProfileMenuEnum';
import {ProfileIcon, StyledInitials} from '../../styles';
import CustomFooter from './CustomFooter';
import {StyledAlertInfo, StyledCardContent} from './styles';
import {BeneficiaryInfoProps} from './types';
import {useLocation} from 'react-router-dom';

const {responsibleWithoutBenefits, changeBeneficiary} = translations['pt-br'].commons;
const {socialName} = translations['pt-br'].others.profileMenu;
const {cancelContractIn, cancelContract} = translations['pt-br'].commons;
export function BeneficiaryInfo({hasSocialNameToolTip, goTo}: BeneficiaryInfoProps) {
	const theme = useTheme();
	const {pathname} = useLocation();
	const isProfileRoute = pathname === ProfileEnum.PROFILE;
	const {data: healthCardData, isError, isLoading} = useGetHealthCardQuery();
	const {beneficiary, isResponsibleWithoutBenefits} = useAccountInformation();
	const {isValid, contractCancellationDate, isContractSuspended} = useContractStatus();

	const cancelContractLabel = contractCancellationDate ? cancelContractIn : cancelContract;
	const alertDate = !isValid ? contractCancellationDate : null;
	const showCancelContractAlert = (!isValid || isContractSuspended) && !isProfileRoute;

	if (isLoading) {
		return <Skeleton variant="rectangular" height={45} />;
	}

	if (isError || !healthCardData) {
		return null;
	}

	const {flPlanoIntegrado, cdModeloCarteira} = healthCardData;

	return (
		<Box width="100%" sx={{position: 'relative'}}>
			<StyledCardContent>
				<ProfileIcon alt="Profile Avatar" sx={{width: 56, height: 56, mr: 2}}>
					<StyledInitials color="primary" sx={{fontSize: 26}}>
						{getInitials(beneficiary?.nmSocialUsuarioC || beneficiary?.nmUsuarioC)}
					</StyledInitials>
				</ProfileIcon>
				<Stack sx={{flex: 1}}>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}>
						<Stack direction="row" alignItems="center" gap={0.5}>
							<Typography
								variant="caption"
								fontSize="0.625rem"
								fontWeight="600"
								color="text.primary">
								{formatText(beneficiary?.tipoUsuarioC)}
							</Typography>
							<Box
								sx={{
									width: 4,
									height: 4,
									borderRadius: '50%',
									bgcolor: 'text.primary',
								}}
							/>
							<Typography
								variant="caption"
								fontSize="0.625rem"
								fontWeight="600"
								color="text.primary">
								{planTypeOptions?.[beneficiary?.tipoPlanoC]?.label}
							</Typography>
						</Stack>

						{hasSocialNameToolTip ? (
							<Tooltip
								enterTouchDelay={0}
								slotProps={{
									popper: {
										sx: {
											[`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
												{
													marginTop: 0,
													backgroundColor:
														theme.palette.other.snackbarBackground,
													padding: '16px 12px',
													fontSize: '1rem',
												},
											[`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.arrow}`]:
												{
													color: theme.palette.other.snackbarBackground,
												},
										},
									},
								}}
								placement="bottom-end"
								arrow
								title={socialName}
								data-testid={ProfileMenuEnum.SOCIAL_NAME}>
								<IconButton sx={{position: 'absolute', right: 1}}>
									<Icon
										name="info-outlined"
										size={16}
										color={theme.palette.primary.main}
									/>
								</IconButton>
							</Tooltip>
						) : null}
					</Box>
					<Typography fontWeight={600} color="text.primary">
						{formatText(beneficiary?.nmSocialUsuarioC || beneficiary?.nmUsuarioC)}
					</Typography>

					<Button
						sx={{lineHeight: 'normal', width: 'max-content', p: 0, fontSize: '0.75rem'}}
						variant="text"
						size="small"
						onClick={goTo}
						endIcon={
							<Icon
								name="chevron-right-outlined"
								size={16}
								style={{marginLeft: -8}}
							/>
						}>
						{changeBeneficiary}
					</Button>
				</Stack>
			</StyledCardContent>
			{showCancelContractAlert ? (
				<StyledAlertInfo severity="warning">
					<AlertInfo.Description>
						<Typography fontSize="0.875rem" component="span">
							{cancelContractLabel}
							<Typography component="span" fontSize="inherit" fontWeight={700}>
								{alertDate
									? stringfyDate(alertDate, DateFormatEnum.DATE_FORMAT_8)
									: ''}
							</Typography>
						</Typography>
					</AlertInfo.Description>
				</StyledAlertInfo>
			) : null}
			<CustomFooter flPlanoIntegrado={flPlanoIntegrado} planType={cdModeloCarteira} />
			{isResponsibleWithoutBenefits ? (
				<AlertInfo.Root
					severity="info"
					sx={{
						whiteSpace: 'normal',
						borderRadius: 1.5,
						mt: 1,
					}}
					data-testid={ProfileMenuEnum.FINANCIAL_RESPONSIBLE}>
					<AlertInfo.Description>{responsibleWithoutBenefits}</AlertInfo.Description>
				</AlertInfo.Root>
			) : null}
		</Box>
	);
}
